<template>
    <div class="min-h-screen">
        <NuxtRouteAnnouncer />
        <NuxtLoadingIndicator color="#d8a595" :height="5" />
        <NuxtLayout>
            <main class="flex grow flex-col">
                <NuxtPage />
            </main>
        </NuxtLayout>
        
    </div>
</template>

<script setup lang="ts">
import { withBase, withoutTrailingSlash } from 'ufo';
import { description, locations, qAndA, title, address } from '~/fixtures';

defineOptions({
    name: 'App'
});

// defer loading resource
onNuxtReady(async () => {
    const api = useApi();
    const { data } = useNuxtData('products');
    const productStore = useProductStore();

    // data may already be there due to SSR (products page)
    if (!data.value?.length) {
        productStore.productsLoading = true;
        // preload products in anticipation of visiting the /products page
        data.value = await getAllProducts(api)
            .then(products => {
                productStore.productsLoaded = true;
                return products;
            })
            .finally(() => productStore.productsLoading = false);
    }

    // assign to product store too for backwards compatibility (product favourites using it...)
    productStore.products = data.value;
});

const productStore = useProductStore();
const router = useRouter();
const config = useRuntimeConfig();
const breadcrumbs = computed(() => {
    if (router.currentRoute.value.fullPath === '/') {
        return [{
            name: 'Home',
            item: '/'
        }];
    }

    const parts = router.currentRoute.value.fullPath.split('/');

    return parts.map((part, i) => {
        return {
            name: i === 0
                ? 'Home'
                : part.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
            item: i === 0 ? '/' : parts.slice(0, i + 1).join('/')
        };
    });
});

const image = computed(() => {
    if (productStore.product?.images.length) {
        // todo - update this to weserve
        return productStore.product.images.at(0)!.source;
    }

    return '/images/logo.jpg';
});
const url = computed(() => withBase(router.currentRoute.value.fullPath, config.public.siteUrl));
const canonicalUrl = computed(
    () => withoutTrailingSlash(withBase(router.currentRoute.value.path, config.public.siteUrl))
);

/* eslint-disable @typescript-eslint/naming-convention */
useSeoMeta({
    title,
    themeColor: '#D8A595',
    msapplicationTileColor: '#da532c',
    appleMobileWebAppTitle: title,
    applicationName: title,
    colorScheme: 'light',
    google: 'nositelinkssearchbox',
    // robots are handled by @nuxtjs/seo robots module via X-Robots-Tag header
    ogType: 'website',
    ogImage: image,
    ogUrl: url,
    ogImageHeight: 1137,
    ogImageWidth: 1137,
    ogImageAlt: title + '\' Logo',
    ogImageUrl: image,
    ogImageType: 'image/jpeg',
    ogLocale: 'en_GB',
    ogSiteName: 'Cina Delicious Cakes',
    ogImageSecureUrl: image,
    ogDeterminer: '',
    twitterCard: 'summary',
    twitterImage: image,
    twitterImageAlt: title + '\' Logo'
});
useSchemaOrg([
    defineLocalBusiness({
        openingHoursSpecification: [
            // {
            //     dayOfWeek: 'Monday',
            //     opens: '00:00',
            //     closes: '00:00'
            // },
            // {
            //     dayOfWeek: 'Tuesday',
            //     opens: '00:00',
            //     closes: '00:00'
            // },
            // {
            //     dayOfWeek: 'Wednesday',
            //     opens: '00:00',
            //     closes: '00:00'
            // },
            // {
            //     dayOfWeek: 'Thursday',
            //     opens: '00:00',
            //     closes: '00:00'
            // },
            // {
            //     dayOfWeek: 'Friday',
            //     opens: '00:00',
            //     closes: '00:00'
            // },
            {
                dayOfWeek: 'Saturday',
                opens: '10:00',
                closes: '17:00'
            },
            {
                dayOfWeek: 'Sunday',
                opens: '10:00',
                closes: '17:00'
            }
        ],
        currenciesAccepted: 'GBP',
        priceRange: '££',
        email: 'cinanorwich@gmail.com',
        name: title,
        '@type': ['Organization', 'LocalBusiness', 'FoodEstablishment', 'Bakery'],
        description,
        servesCuisine: 'European',
        acceptsReservations: false,
        sameAs: [
            'https://www.facebook.com/CinaCDC',
            'https://www.instagram.com/cina_delicious_cakes/',
            'https://www.tiktok.com/@cinasdeliciouscakes'
        ],
        image: defineImage({
            url: 'images/logo.jpg',
            contentUrl: withBase('images/logo.jpg', config.public.siteUrl),
            width: 1137,
            height: 1137,
            inLanguage: 'en-GB',
            caption: title + '\' Logo'
        }),
        address: defineAddress({
            addressCountry: 'GB',
            addressLocality: address.city,
            addressRegion: address.county,
            postalCode: address.postcode,
            streetAddress: address.street
        }),
        geo: {
            '@type': 'GeoCoordinates',
            latitude: 52.66922089212267,
            longitude: 1.2930680535440677
        },
        areaServed: locations
    }),
    defineWebSite({
        name: title,
        url: config.public.siteUrl,
        description: description,
        inLanguage: 'en-GB',
        image: defineImage({
            url: 'images/logo.jpg',
            contentUrl: withBase('images/logo.jpg', config.public.siteUrl),
            width: 1137,
            height: 1137,
            inLanguage: 'en-GB',
            caption: title + '\' Logo'
        })
    }),
    ...qAndA.map(({ question, answer }) => {
        return defineQuestion({
            name: question,
            acceptedAnswer: {
                text: answer
            }
        });
    }),
    defineBreadcrumb({
        itemListElement: breadcrumbs.value
    })
]);
useHead({
    // pages already include the site name in their title, so disable the auto-appended suffix
    titleTemplate: '%s',
    link: [
        {
            rel: 'canonical',
            // Google might not be able to tell what is the original url if a query string is present
            href: canonicalUrl
        }
    ],
    templateParams: {
        schemaOrg: {
            host: config.public.siteUrl,
            path: router.currentRoute.value.path
        }
    }
});
</script>
